import React, { useState, useMemo } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { LoaderCircleV2, UI_CAPTION, colors2 } from "oolib";
import { PillDropdown } from "../PillDropdown";
import { SimpleBarChart } from "../SimpleBarChart";
import { DashboardWidgetShell } from "../DashboardWidgetShell";
import {
  StyledFreshness,
  StyledTopBarRow,
  StyledTopBar,
  StyledColumns,
  StyledColumn,
  FRESH_COLOR,
  STALE_COLOR,
  StyledTopBarLabel,
} from "./styled";

const ALL_GROUP = { value: "__all__", display: "All Themes" };

const INTERVAL_OPTIONS = [
  { value: "hour", display: "Hourly" },
  { value: "day", display: "Daily" },
  { value: "week", display: "Weekly" },
  { value: "month", display: "Monthly" },
  { value: "year", display: "Yearly" },
];

const fetchTagHealth = async ({ queryKey }) => {
  const [, , { interval, group }] = queryKey;
  const params = new URLSearchParams({ interval });
  if (group && group !== "__all__") params.set("group", group);
  const res = await axios.get(`/api/playground/dashboardTagHealth?${params}`);
  return res.data;
};

export function Freshness() {
  const [interval, setInterval] = useState({ value: "month", display: "Monthly" });
  const [group, setGroup] = useState(ALL_GROUP);

  const query = useQuery({
    queryKey: ["tagHealth", "freshness-widget", { interval: interval.value, group: group.value }],
    queryFn: fetchTagHealth,
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
  });

  const groupOptions = useMemo(() => {
    const groups = query.data?.availableGroups;
    if (!groups) return [ALL_GROUP];
    return [ALL_GROUP, ...groups.map((g) => ({ value: g, display: g }))];
  }, [query.data?.availableGroups]);

  const freshTopics = (query.data?.gainers || []).map((t) => ({ name: t.display, count: t.count })).sort((a, b) => b.count - a.count);
  const staleTopics = (query.data?.losers || []).map((t) => ({ name: t.display, count: t.count })).sort((a, b) => b.count - a.count);
  const freshCount = freshTopics.reduce((sum, t) => sum + t.count, 0);
  const staleCount = staleTopics.reduce((sum, t) => sum + t.count, 0);
  const total = freshCount + staleCount || 1;
  const freshPercent = (freshCount / total) * 100;
  const stalePercent = (staleCount / total) * 100;

  const filterBar = (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
      <PillDropdown value={group} onChange={(id, val) => setGroup(val)} options={groupOptions} alignDropdown="right" />
      <PillDropdown value={interval} onChange={(id, val) => setInterval(val)} options={INTERVAL_OPTIONS} alignDropdown="right" />
    </div>
  );

  return (
    <DashboardWidgetShell title="Freshness" headerRight={filterBar}>
      {query.isLoading || query.isFetching ? (
        <div style={{ padding: "2rem", textAlign: "center" }}><LoaderCircleV2 /></div>
      ) : !freshTopics.length && !staleTopics.length ? (
        <UI_CAPTION color={colors2.grey40}>No data available</UI_CAPTION>
      ) : (
        <StyledFreshness>
          <StyledTopBarRow>
            {freshPercent > 0 && (
              <StyledTopBar $variant="fresh" $percent={freshPercent}>
                <StyledTopBarLabel $variant="fresh">Fresh</StyledTopBarLabel>
              </StyledTopBar>
            )}
            {stalePercent > 0 && (
              <StyledTopBar $variant="stale" $percent={stalePercent}>
                <StyledTopBarLabel $variant="stale">Stale</StyledTopBarLabel>
              </StyledTopBar>
            )}
          </StyledTopBarRow>
          <StyledColumns $singleColumn={!freshTopics.length || !staleTopics.length}>
            {freshTopics.length > 0 && (
              <StyledColumn>
                <SimpleBarChart
                  data={freshTopics.slice(0, 5)}
                  barColor={FRESH_COLOR}
                />
              </StyledColumn>
            )}
            {staleTopics.length > 0 && (
              <StyledColumn>
                <SimpleBarChart
                  data={staleTopics.slice(0, 5)}
                  barColor={STALE_COLOR}
                />
              </StyledColumn>
            )}
          </StyledColumns>
        </StyledFreshness>
      )}
    </DashboardWidgetShell>
  );
}
